/**
 * Broker Connections Service
 *
 * Wraps all /broker/auth/... API calls for the enhanced multi-broker auth flow.
 * The older /brokers/:id CRUD routes are still used for refresh / test / settings / delete.
 */
import api from '@services/enhancedApiService'

export const brokerConnectionsService = {
  /**
   * List all broker connections for the current user.
   * Uses the newer /broker/connections endpoint that supports all 36+ brokers.
   * @returns {Promise} axios response: { data: { success, data: Connection[] } }
   */
  getConnections: () => api.get('/broker/connections'),

  /**
   * Get lightweight account snapshot for a connection (margin, holdings, positions).
   * Cached 3 min on the server — safe to call on card mount.
   * @param {string} connectionId
   */
  getSnapshot: (connectionId, refresh = false) => api.get(`/brokers/${connectionId}/snapshot`, { params: { refresh } }),

  /**
   * Get the list of brokers that x3algo has configured (enabled: true in server config).
   * Returns { data: { success, data: [{ id, name, available, logo, ... }] } }
   * Only brokers with available: true are ready for users to connect.
   */
  getSupportedBrokers: () => api.get('/brokers/supported'),

  /**
   * Get an OAuth2 authorization URL for a broker.
   * Returns { url, state } — redirect the user to `url`.
   * @param {string} broker  — broker slug e.g. 'zerodha'
   */
  getOAuthUrl: (broker) => api.get(`/broker/auth/${broker}/url`),

  /**
   * Initiate a Publisher OAuth login for brokers with authType 'publisher'.
   * POSTs credentials to broker-specific URL endpoint, gets a consent/redirect URL back.
   * @param {string} broker — e.g. 'dhan'
   * @param {object} data   — e.g. { apiKey, apiSecret, clientId }
   */
  initPublisherLogin: (broker, data) => api.post(`/broker/auth/${broker}/url`, data),

  /**
   * Start a Dhan consent flow using its encrypted saved credentials.
   * @param {object} credentialOverrides
   */
  initPublisherLoginWithSavedCredentials: (credentialOverrides = {}) =>
    api.post('/broker/auth/dhan/reconnect-url', credentialOverrides),

  /**
   * Connect a broker using form-based credentials (TOTP or API-key).
   * POST /broker/auth/:broker/connect
   * @param {string} broker
   * @param {object} credentials  — key/value map matching the broker's field list
   */
  connectWithCredentials: (broker, credentials) =>
    api.post(`/broker/auth/${broker}/connect`, credentials),

  /**
   * Reconnect with server-stored encrypted credentials and only send values
   * that must be renewed for this login (for example, a current TOTP code).
   * @param {string} broker
   * @param {object} credentialOverrides
   */
  reconnectWithSavedCredentials: (broker, credentialOverrides = {}) =>
    api.post(`/broker/auth/${broker}/reconnect`, credentialOverrides),

  /**
   * Soft-disconnect a broker (marks the connection inactive, keeps encrypted credentials).
   * DELETE /broker/auth/:broker
   * @param {string} broker
   */
  disconnect: (broker) => api.delete(`/broker/auth/${broker}`),

  /**
   * Test an existing broker connection health by broker name.
   * GET /broker/auth/:broker/test
   * @param {string} broker
   */
  testByBroker: (broker) => api.get(`/broker/auth/${broker}/test`),

  // ── CRUD routes using connection _id (legacy /api/brokers endpoints) ─────────

  /**
   * Refresh the session token for a connection (by DB _id).
   * POST /brokers/:id/refresh
   */
  refreshSession: (connectionId) => api.post(`/brokers/${connectionId}/refresh`),

  /**
   * Test a connection by its database _id.
   * POST /brokers/:id/test
   */
  testById: (connectionId) => api.post(`/brokers/${connectionId}/test`),

  /**
   * Update connection settings.
   * PATCH /brokers/:id/settings
   * @param {string} connectionId
   * @param {{ autoReconnect?: boolean, notifyOnExpiry?: boolean, allowPaperTrade?: boolean }} settings
   */
  updateSettings: (connectionId, settings) =>
    api.patch(`/brokers/${connectionId}/settings`, { settings }),

  /**
   * Permanently delete a broker connection and all stored credentials.
   * DELETE /brokers/:id/permanent
   */
  deleteConnection: (connectionId) => api.delete(`/brokers/${connectionId}/permanent`),
}
